import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { 
  ArrowLeft, 
  Save,
  XCircle,
  AlertTriangle
} from 'lucide-react'
import { toast } from 'react-toastify'

interface PurchaseOrderEditData {
  id: string
  poNumber: string
  status: string
  totalAmount: number
  currency: string
  notes?: string
  createdAt: string
  updatedAt: string
  buyer: {
    id: string
    email: string
  }
  vendor: {
    id: string
    email: string
  }
}

interface EditFormData {
  totalAmount: string
  currency: string
  notes: string
}

const PurchaseOrderEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderEditData | null>(null)
  const [formData, setFormData] = useState<EditFormData>({
    totalAmount: '',
    currency: 'USD',
    notes: ''
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }

    if (!id) {
      navigate('/enterprise/post-quote-activity/purchase-orders')
      return
    }

    loadPurchaseOrder()
  }, [user, navigate, id])

  const loadPurchaseOrder = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiService.get(`/api/post-quote-activity/purchase-orders/${id}`) as any
      
      if (response.success) {
        const po = response.data
        setPurchaseOrder(po)
        
        // Populate form data
        setFormData({
          totalAmount: (po.totalAmount / 100).toString(), // Convert from cents to dollars
          currency: po.currency || 'USD',
          notes: po.notes || ''
        })
      } else {
        setError('Failed to load purchase order')
      }
    } catch (err: any) {
      console.error('Error loading purchase order:', err)
      if (err.response?.status === 404) {
        setError('Purchase order not found')
      } else if (err.response?.status === 403) {
        setError('Access denied')
      } else {
        setError('Failed to load purchase order')
      }
      toast.error(err.response?.data?.message || 'Failed to load purchase order')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!purchaseOrder) return

    // Validation
    const amount = parseFloat(formData.totalAmount)
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid total amount')
      return
    }

    try {
      setSaving(true)
      
      const updateData = {
        totalAmount: Math.round(amount * 100), // Convert to cents
        currency: formData.currency,
        notes: formData.notes.trim() || undefined
      }
      
      const response = await apiService.patch(`/api/post-quote-activity/purchase-orders/${id}`, updateData) as any
      
      if (response.success) {
        toast.success('Purchase order updated successfully')
        navigate(`/enterprise/post-quote-activity/purchase-orders/${id}`)
      } else {
        toast.error('Failed to update purchase order')
      }
    } catch (err: any) {
      console.error('Error updating purchase order:', err)
      toast.error(err.response?.data?.message || 'Failed to update purchase order')
    } finally {
      setSaving(false)
    }
  }

  const canEdit = () => {
    if (!purchaseOrder || !user) return false
    
    // Only buyers can edit their own POs, and only if status allows
    const isBuyer = purchaseOrder.buyer.id === user.id
    const editableStatuses = ['PENDING', 'IN_REVIEW']
    
    return isBuyer && editableStatuses.includes(purchaseOrder.status)
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="space-y-4">
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-24 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !purchaseOrder) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error Loading Purchase Order</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/enterprise/post-quote-activity/purchase-orders')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Purchase Orders
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!canEdit()) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Cannot Edit Purchase Order</h3>
          <p className="mt-1 text-sm text-gray-500">
            {purchaseOrder.buyer.id !== user?.id 
              ? 'Only the buyer can edit this purchase order'
              : `Purchase orders with status "${purchaseOrder.status}" cannot be edited`
            }
          </p>
          <div className="mt-6 space-x-3">
            <button
              onClick={() => navigate(`/enterprise/post-quote-activity/purchase-orders/${id}`)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              View Details
            </button>
            <button
              onClick={() => navigate('/enterprise/post-quote-activity/purchase-orders')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to List
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/enterprise/post-quote-activity/purchase-orders/${id}`)}
            className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Purchase Order</h1>
            <p className="text-sm text-gray-600">PO Number: {purchaseOrder.poNumber}</p>
          </div>
        </div>
      </div>

      {/* Edit Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Total Amount */}
          <div>
            <label htmlFor="totalAmount" className="block text-sm font-medium text-gray-700 mb-2">
              Total Amount *
            </label>
            <div className="flex">
              <select
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                className="rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
              </select>
              <input
                type="number"
                id="totalAmount"
                name="totalAmount"
                value={formData.totalAmount}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                required
                className="flex-1 rounded-r-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Enter the total amount in the selected currency
            </p>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="Add any additional notes or comments..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional notes about this purchase order
            </p>
          </div>

          {/* Current Status Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Purchase Order Status</h4>
            <p className="text-sm text-blue-700">
              Current status: <span className="font-medium">{purchaseOrder.status}</span>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {purchaseOrder.status === 'PENDING' && 'Waiting for vendor acceptance'}
              {purchaseOrder.status === 'IN_REVIEW' && 'Under vendor review'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/enterprise/post-quote-activity/purchase-orders/${id}`)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PurchaseOrderEditPage